import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Lock, Hammer, Receipt } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d.length === 10 ? d + 'T12:00:00' : d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

export default function ContractorInvoiceShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/moms-house/contractor-invoices/share/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.status === 404 ? 'Invoice link not found or expired.' : 'Could not load invoice.');
      } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) return <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">Loading...</div>;
  if (error) return (
    <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center p-6">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-sm text-center">
        <Lock className="w-10 h-10 mx-auto text-zinc-500 mb-3" />
        <p className="text-zinc-300">{error}</p>
      </div>
    </div>
  );

  const { invoice, totals } = data;
  const pct = totals.total_invoice > 0 ? Math.min(100, (totals.spent / totals.total_invoice) * 100) : 0;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="text-center mb-5">
          <Hammer className="w-9 h-9 mx-auto text-amber-400 mb-2" />
          <h1 className="text-xl sm:text-2xl font-bold">{invoice.name}</h1>
          {invoice.contractor_name && <p className="text-amber-300 text-sm mt-1">Contractor: {invoice.contractor_name}</p>}
          {invoice.notes && <p className="text-zinc-400 text-xs mt-2">{invoice.notes}</p>}
        </div>

        {/* STARTING BALANCE banner */}
        <div className="bg-gradient-to-r from-amber-700 to-orange-700 border border-amber-400/40 rounded-xl p-4 sm:p-5 mb-4 shadow-lg">
          <p className="text-amber-100 text-[11px] uppercase tracking-wider font-bold">Starting Balance</p>
          <p className="text-white text-3xl sm:text-4xl font-extrabold mt-0.5 leading-none">{fmt(totals.total_invoice)}</p>
          <p className="text-amber-100/80 text-xs mt-2">Total invoice amount</p>
        </div>

        {/* Subtotals */}
        <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 mb-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-2.5">
              <p className="text-blue-400 text-[10px] font-semibold uppercase">Materials</p>
              <p className="text-white font-bold text-sm mt-0.5">− {fmt(totals.materials_total)}</p>
            </div>
            <div className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-2.5">
              <p className="text-purple-400 text-[10px] font-semibold uppercase">Labor</p>
              <p className="text-white font-bold text-sm mt-0.5">− {fmt(totals.labor_total)}</p>
            </div>
            <div className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-2.5">
              <p className="text-amber-300 text-[10px] font-semibold uppercase">Total Spent</p>
              <p className="text-white font-bold text-sm mt-0.5">{fmt(totals.spent)}</p>
            </div>
          </div>
          <div className="w-full bg-zinc-900 rounded-full h-2 mt-3 overflow-hidden">
            <div className="bg-amber-500 h-2" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* Materials list */}
        <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2 mb-3"><Receipt className="w-4 h-4 text-blue-400" /> Material Receipts</h3>
          {(invoice.materials || []).length === 0 ? (
            <p className="text-zinc-500 text-xs text-center py-3">None</p>
          ) : (
            <div className="space-y-1.5">
              {(invoice.materials || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-2 bg-zinc-800/40 border border-zinc-700 rounded-lg px-3 py-2 text-xs flex-wrap">
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <span className="text-blue-400 font-bold text-sm">{fmt(m.amount)}</span>
                    <span className="text-white truncate">{m.store}</span>
                    <span className="text-zinc-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(m.date)}</span>
                    {m.notes && <span className="text-zinc-500 truncate">— {m.notes}</span>}
                  </div>
                </div>
              ))}
              <div className="flex justify-end pt-1 text-xs text-zinc-400">Subtotal: <span className="text-white font-bold ml-1">{fmt(totals.materials_total)}</span></div>
            </div>
          )}
        </div>

        {/* Labor list */}
        <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2 mb-3"><Hammer className="w-4 h-4 text-purple-400" /> Labor Payments</h3>
          {(invoice.labor_payments || []).length === 0 ? (
            <p className="text-zinc-500 text-xs text-center py-3">None</p>
          ) : (
            <div className="space-y-1.5">
              {(invoice.labor_payments || []).map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-2 bg-zinc-800/40 border border-zinc-700 rounded-lg px-3 py-2 text-xs flex-wrap">
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <span className="text-purple-400 font-bold text-sm">{fmt(l.amount)}</span>
                    <span className="px-2 py-0.5 bg-purple-900/50 text-purple-300 rounded-full text-[10px] font-semibold">{l.method}</span>
                    <span className="text-zinc-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(l.date)}</span>
                    {l.notes && <span className="text-zinc-500 truncate">— {l.notes}</span>}
                  </div>
                </div>
              ))}
              <div className="flex justify-end pt-1 text-xs text-zinc-400">Subtotal: <span className="text-white font-bold ml-1">{fmt(totals.labor_total)}</span></div>
            </div>
          )}
        </div>

        {/* REMAINING BALANCE footer */}
        <div className={`rounded-xl p-4 sm:p-5 mb-4 border-2 shadow-lg ${totals.remaining > 0 ? 'bg-gradient-to-r from-green-700 to-emerald-700 border-green-400/40' : 'bg-gradient-to-r from-blue-700 to-indigo-700 border-blue-400/40'}`}>
          <p className={`text-[11px] uppercase tracking-wider font-bold ${totals.remaining > 0 ? 'text-green-100' : 'text-blue-100'}`}>Remaining Balance</p>
          <p className="text-white text-3xl sm:text-4xl font-extrabold mt-0.5 leading-none">{fmt(totals.remaining)}</p>
          <p className={`text-xs mt-2 ${totals.remaining > 0 ? 'text-green-100/80' : 'text-blue-100/80'}`}>
            {fmt(totals.total_invoice)} starting − {fmt(totals.spent)} spent {totals.remaining <= 0 ? '• Project fully covered ✓' : ''}
          </p>
        </div>

        <p className="text-zinc-600 text-[11px] text-center pb-4">Read-only invoice • Generated {formatDate(invoice.created_at)}</p>
      </div>
    </div>
  );
}
